import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ConfirmDialog,
  EmptyState,
  FilterBar,
  FormDialog,
  LoadingState,
  PageHeader,
  ResponsiveTable,
  StatusChip,
  useFeedback,
} from '../components/ui';
import PendingApprovals from '../components/PendingApprovals';
import { UserContext } from '../contexts/UserContext';
import { accessPresentation, canReviewApprovals, canSeeManagerNavigation, readSessionUser, roleLabel } from '../utils/sessionUser';

import { API_BASE_URL } from '../config/apiBase';

const initialFormState = {
  nome: '',
  email: '',
  senha: '',
  cargo: '',
  matricula: '',
  curso: '',
  periodo: '',
  horario_trabalho: '',
  local_trabalho: '',
  tipo: 'funcionario',
  organizacao_id: '',
  auth_provider: 'local',
  provider_id: '',
};

function blankToNull(value) {
  const text = typeof value === 'string' ? value.trim() : value;
  return text ? text : null;
}

function UserManagement() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useFeedback();
  const { users, loading, usersError, addUser, editUser, deleteUser, fetchUsers } = useContext(UserContext);
  const session = readSessionUser();
  const isAdmin = canReviewApprovals(session);
  const isManager = session?.tipo === 'gerente';
  const canManage = canSeeManagerNavigation(session) && usersError !== 'forbidden';

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [organizations, setOrganizations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [section, setSection] = useState('users');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [orgFilter, setOrgFilter] = useState('todas');
  const openedFromNavigation = useRef(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_BASE_URL}/organizations`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setOrganizations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao carregar organizações:', err);
        notify('Não foi possível carregar as organizações.', 'error');
      }
    };

    fetchOrganizations();
  }, [notify]);

  const loadPending = async () => {
    if (!isAdmin) return;
    const token = localStorage.getItem('token');
    const { data } = await axios.get(`${API_BASE_URL}/users/pending`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    setPendingRequests(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!isAdmin) return undefined;
    loadPending().catch(() => notify('Não foi possível carregar os cadastros pendentes.', 'error'));
    return undefined;
    // A lista é recarregada depois de cada decisão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const organizationMap = useMemo(
    () => organizations.reduce((map, org) => ({ ...map, [org.id]: org.nome }), {}),
    [organizations]
  );

  const handleOpenModal = (user = null) => {
    setEditMode(!!user);
    setCurrentUser(user);
    const lockedOrg = isManager ? (session?.organizacao_id || '') : '';

    if (user) {
      setFormData({
        nome: user.nome || '',
        email: user.email || '',
        senha: '',
        cargo: user.cargo || '',
        matricula: user.matricula || '',
        curso: user.curso || '',
        periodo: user.periodo || '',
        horario_trabalho: user.horario_trabalho || '',
        local_trabalho: user.local_trabalho || '',
        tipo: isManager ? 'funcionario' : (user.tipo || 'funcionario'),
        organizacao_id: isManager ? lockedOrg : (user.organizacao_id || ''),
        auth_provider: isManager ? 'local' : (user.auth_provider || 'local'),
        provider_id: user.provider_id || '',
      });
    } else {
      setFormData({
        ...initialFormState,
        organizacao_id: isManager ? lockedOrg : (organizations[0]?.id ?? ''),
        tipo: 'funcionario',
        auth_provider: 'local',
      });
    }

    setOpenModal(true);
  };

  useEffect(() => {
    if (!openModal || editMode || isManager || formData.organizacao_id || organizations.length === 0) return;
    setFormData((prev) => ({ ...prev, organizacao_id: organizations[0].id }));
  }, [openModal, editMode, isManager, formData.organizacao_id, organizations]);

  useEffect(() => {
    if (!location.state?.create || openedFromNavigation.current || !canManage) return;
    openedFromNavigation.current = true;
    handleOpenModal();
    navigate('/users', { replace: true, state: null });
    // A abertura usa as organizações já carregadas neste render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, canManage, organizations]);

  const handleCloseModal = () => {
    if (submitting) return;
    setOpenModal(false);
    setCurrentUser(null);
    setFormData(initialFormState);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesTerm = !term
        || String(user.nome || '').toLowerCase().includes(term)
        || String(user.email || '').toLowerCase().includes(term);
      const matchesRole = roleFilter === 'todos' || user.tipo === roleFilter;
      const matchesOrg = orgFilter === 'todas' || String(user.organizacao_id) === String(orgFilter);
      return matchesTerm && matchesRole && matchesOrg;
    });
  }, [users, search, roleFilter, orgFilter]);

  const handleSubmit = async () => {
    const nome = formData.nome.trim();
    const email = formData.email.trim();
    if (!nome || !email) {
      notify('Nome e e-mail são obrigatórios.', 'warning');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notify('Informe um e-mail válido.', 'warning');
      return;
    }
    if (formData.auth_provider === 'local' && !editMode && !formData.senha) {
      notify('Informe uma senha para usuários com autenticação local.', 'warning');
      return;
    }
    if (!isManager && formData.auth_provider === 'google' && !formData.provider_id.trim()) {
      notify('Informe o ID do usuário no provedor externo.', 'warning');
      return;
    }
    if (formData.auth_provider === 'local' && editMode && currentUser?.auth_provider === 'google' && !formData.senha) {
      notify('Informe uma senha ao migrar o usuário para autenticação local.', 'warning');
      return;
    }

    const personal = {
      nome,
      email,
      cargo: blankToNull(formData.cargo),
      matricula: blankToNull(formData.matricula),
      curso: blankToNull(formData.curso),
      periodo: blankToNull(formData.periodo),
      horario_trabalho: blankToNull(formData.horario_trabalho),
      local_trabalho: blankToNull(formData.local_trabalho),
    };

    let payload = personal;
    if (!isManager) {
      payload = {
        ...personal,
        tipo: formData.tipo,
        organizacao_id: formData.organizacao_id || null,
        auth_provider: formData.auth_provider,
        provider_id: formData.auth_provider === 'google' ? formData.provider_id.trim() : null,
      };
    }
    if (formData.auth_provider === 'local' && formData.senha) {
      payload = { ...payload, senha: formData.senha };
    }

    setSubmitting(true);
    try {
      if (editMode && currentUser) {
        await editUser(currentUser.id, payload);
        notify('Usuário atualizado.', 'success');
      } else {
        await addUser(payload);
        notify('Usuário adicionado.', 'success');
      }
      setOpenModal(false);
      setCurrentUser(null);
      setFormData(initialFormState);
    } catch (err) {
      const message = err?.response?.data?.message || 'Não foi possível salvar o usuário.';
      notify(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteUser(pendingDelete.id);
      notify('Usuário inativado.', 'success');
      setPendingDelete(null);
    } catch (err) {
      const message = err?.response?.data?.message || 'Não foi possível inativar o usuário.';
      notify(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && users.length === 0 && !usersError) {
    return <LoadingState label="Carregando usuários" />;
  }

  if (usersError === 'forbidden') {
    return (
      <EmptyState
        title="Sem permissão para gerenciar usuários."
        description="A listagem fica disponível para administrador e gerente. O servidor continua recusando o acesso direto."
      />
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Gerenciamento de usuários"
        subtitle="Contas internas do PontoNow. Participantes do hackathon não aparecem aqui."
        actions={canManage ? (
          <Button variant="contained" onClick={() => handleOpenModal()}>
            Adicionar usuário
          </Button>
        ) : null}
      />

      {usersError === 'error' ? (
        <EmptyState
          title="Não foi possível carregar os usuários."
          description="A página permanece disponível. Atualize depois de restabelecer a comunicação com o servidor."
        />
      ) : (
        <>
          {isAdmin ? (
            <Tabs
              value={section}
              onChange={(_event, value) => setSection(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              <Tab value="users" label="Usuários" />
              <Tab
                value="pending"
                label={(
                  <Badge color="warning" badgeContent={pendingRequests.length} invisible={pendingRequests.length === 0}>
                    <Box component="span" sx={{ pr: pendingRequests.length ? 2 : 0 }}>Cadastros pendentes</Box>
                  </Badge>
                )}
              />
            </Tabs>
          ) : null}
          {section === 'pending' && isAdmin ? (
            <PendingApprovals
              requests={pendingRequests}
              organizations={organizations}
              isAdmin={isAdmin}
              onApprove={async (id, body) => {
                const token = localStorage.getItem('token');
                await axios.patch(`${API_BASE_URL}/users/${id}/approval`, { action: 'approve', ...body }, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                setPendingRequests((current) => current.filter((item) => item.id !== id));
                notify('Cadastro aprovado.', 'success');
                fetchUsers().catch(() => {});
              }}
              onReject={async (id, body) => {
                const token = localStorage.getItem('token');
                await axios.patch(`${API_BASE_URL}/users/${id}/approval`, { action: 'reject', ...body }, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                setPendingRequests((current) => current.filter((item) => item.id !== id));
                notify('Cadastro rejeitado.', 'success');
                fetchUsers().catch(() => {});
              }}
            />
          ) : (
          <>
          <FilterBar onSubmit={(event) => event.preventDefault()}>
            <TextField
              label="Nome ou e-mail"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size="small"
              sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}
            />
            <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel id="filtro-perfil">Perfil</InputLabel>
              <Select
                labelId="filtro-perfil"
                label="Perfil"
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="gerente">Gerente</MenuItem>
                <MenuItem value="funcionario">Funcionário</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id="filtro-organizacao">Organização</InputLabel>
              <Select
                labelId="filtro-organizacao"
                label="Organização"
                value={orgFilter}
                onChange={(event) => setOrgFilter(event.target.value)}
              >
                <MenuItem value="todas">Todas</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={String(org.id)}>{org.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterBar>

          {filteredUsers.length === 0 ? (
            <EmptyState
              title={users.length === 0 ? 'Nenhum usuário ativo.' : 'Nenhum usuário encontrado com esses filtros.'}
            />
          ) : compact ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filteredUsers.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    minWidth: 0,
                  }}
                >
                  <UserFacts user={user} organizationMap={organizationMap} />
                  {canManage ? <UserActions user={user} onEdit={handleOpenModal} onDelete={setPendingDelete} /> : null}
                </Box>
              ))}
            </Box>
          ) : (
            <ResponsiveTable minWidth={1180} label="Usuários ativos">
              <TableHead>
                <TableRow>
                  <TableCell>Usuário</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Organização</TableCell>
                  <TableCell>Cargo</TableCell>
                  <TableCell>Curso</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Autenticação</TableCell>
                  <TableCell>Situação</TableCell>
                  {canManage ? <TableCell>Ações</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.nome}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <StatusChip status={user.tipo} label={roleLabel(user.tipo)} />
                    </TableCell>
                    <TableCell>{user.organizacao_nome || organizationMap[user.organizacao_id] || '—'}</TableCell>
                    <TableCell>{user.cargo || '—'}</TableCell>
                    <TableCell>{user.curso || '—'}</TableCell>
                    <TableCell>{user.periodo || '—'}</TableCell>
                    <TableCell>{user.auth_provider === 'google' ? 'Google' : 'Local'}</TableCell>
                    <TableCell><StatusChip {...accessPresentation(user)} /></TableCell>
                    {canManage ? (
                      <TableCell>
                        <UserActions user={user} onEdit={handleOpenModal} onDelete={setPendingDelete} />
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          )}
          </>
          )}
        </>
      )}

      <FormDialog
        open={openModal}
        title={editMode ? 'Editar usuário' : 'Adicionar usuário'}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={submitting ? 'Salvando...' : 'Salvar'}
      >
        <TextField label="Nome" name="nome" value={formData.nome} onChange={handleChange} required fullWidth />
        <TextField label="E-mail" name="email" type="email" value={formData.email} onChange={handleChange} required fullWidth />
        <FormControl fullWidth disabled={isManager}>
          <InputLabel id="tipo-label">Perfil</InputLabel>
          <Select labelId="tipo-label" label="Perfil" name="tipo" value={formData.tipo} onChange={handleChange}>
            {isAdmin || !isManager ? <MenuItem value="admin">Administrador</MenuItem> : null}
            {isAdmin || !isManager ? <MenuItem value="gerente">Gerente</MenuItem> : null}
            <MenuItem value="funcionario">Funcionário</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isManager || organizations.length === 0}>
          <InputLabel id="organizacao-label">Organização</InputLabel>
          <Select
            labelId="organizacao-label"
            label="Organização"
            name="organizacao_id"
            value={formData.organizacao_id}
            onChange={handleChange}
          >
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>{org.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Curso" name="curso" value={formData.curso} onChange={handleChange} fullWidth />
        <TextField label="Período" name="periodo" value={formData.periodo} onChange={handleChange} fullWidth />
        <FormControl fullWidth disabled={isManager}>
          <InputLabel id="auth-provider-label">Autenticação</InputLabel>
          <Select
            labelId="auth-provider-label"
            label="Autenticação"
            name="auth_provider"
            value={formData.auth_provider}
            onChange={(event) => {
              const nextProvider = event.target.value;
              setFormData((prev) => ({
                ...prev,
                auth_provider: nextProvider,
                senha: nextProvider === 'local' ? prev.senha : '',
              }));
            }}
          >
            <MenuItem value="local">Local (senha)</MenuItem>
            {!isManager ? <MenuItem value="google">Google</MenuItem> : null}
          </Select>
        </FormControl>
        {formData.auth_provider === 'google' ? (
          <TextField
            label="ID do Google (provider_id)"
            name="provider_id"
            value={formData.provider_id}
            onChange={handleChange}
            fullWidth
            disabled={isManager}
          />
        ) : (
          <TextField
            label="Senha"
            name="senha"
            type="password"
            value={formData.senha}
            onChange={handleChange}
            fullWidth
            helperText={editMode ? 'Deixe em branco para manter a senha atual.' : 'Obrigatória para autenticação local.'}
          />
        )}
        <TextField label="Cargo" name="cargo" value={formData.cargo} onChange={handleChange} fullWidth />
        <TextField label="Matrícula" name="matricula" value={formData.matricula} onChange={handleChange} fullWidth />
        <TextField label="Horário de trabalho" name="horario_trabalho" value={formData.horario_trabalho} onChange={handleChange} fullWidth />
        <TextField label="Local de trabalho" name="local_trabalho" value={formData.local_trabalho} onChange={handleChange} fullWidth />
      </FormDialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Inativar usuário"
        description={pendingDelete ? `${pendingDelete.nome} deixa de aparecer nas listagens ativas. O registro permanece no banco.` : ''}
        confirmLabel="Inativar"
        destructive
        confirming={deleting}
        onConfirm={handleDelete}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
      />
    </Box>
  );
}

function UserFacts({ user, organizationMap }) {
  return (
    <Box sx={{ mb: 1.5, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>{user.nome}</Typography>
      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
      <Typography variant="body2" color="text.secondary">
        {roleLabel(user.tipo)}
        {' · '}
        {user.organizacao_nome || organizationMap[user.organizacao_id] || 'Sem organização'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {`Cargo: ${user.cargo || '—'} · ${user.auth_provider === 'google' ? 'Google' : 'Local'}`}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {`Curso: ${user.curso || '—'} · Período: ${user.periodo || '—'}`}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <StatusChip {...accessPresentation(user)} />
      </Box>
    </Box>
  );
}

function UserActions({ user, onEdit, onDelete }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Button size="small" variant="outlined" onClick={() => onEdit(user)} aria-label={`Editar ${user.nome}`}>
        Editar
      </Button>
      <Button size="small" variant="contained" color="error" onClick={() => onDelete(user)} aria-label={`Inativar ${user.nome}`}>
        Inativar
      </Button>
    </Box>
  );
}

export default UserManagement;
